import { Button } from '@foam/buoy/src/components/Button/Button';
import { Checkbox } from '@foam/buoy/src/components/Checkbox/Checkbox';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { useCallback, useState } from 'react';
import {
  ArrowLeftIcon,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from 'ui-components';
import IconButton from '~/components/IconButton';
import { SOCIAL_PLATFORMS, SOCIAL_PLATFORMS_NAMES } from '~/constants';
import { getPlatformIcon } from '~/core/utils';

import { useAddContentStore } from '../useAddContentStore';
import { ComparatorType, OptionsValueType, PLATFORM_OPTIONS } from './types';

export const Platforms = () => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    platforms,
    platformOptions,
    setPlatforms,
    setPlatformOptions,
    clearPlatformsAndOptions,
    setFilters,
    removeFilter,
  } = useAddContentStore();

  const handlePlatformDrawerClick = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // Updated handleSelectPlatform: When a platform is selected,
  // add it to the list and include all its options; when unselected, remove both.
  const handleSelectPlatform = useCallback(
    (checked: boolean, value: SOCIAL_PLATFORMS) => {
      let selectedPlatforms = platforms ? platforms.split(',') : [];
      let selectedPlatformOptions = [...platformOptions];

      // Get all option objects for the platform (if any)
      const optionsForPlatform = PLATFORM_OPTIONS[value] || [];

      if (checked) {
        // Add the platform if not already in the state.
        if (!selectedPlatforms.includes(value)) {
          selectedPlatforms.push(value);
        }
        // Add every option for this platform (if not already selected).
        optionsForPlatform.forEach((option) => {
          if (!selectedPlatformOptions.includes(option.value)) {
            selectedPlatformOptions.push(option.value);
          }
        });
      } else {
        // Remove the platform.
        selectedPlatforms = selectedPlatforms.filter((p) => p !== value);
        // Remove all options that belong to this platform.
        const platformOptionValues = optionsForPlatform.map(
          (option: { value: string }) => option.value,
        );
        selectedPlatformOptions = selectedPlatformOptions.filter(
          (opt) => !platformOptionValues.includes(opt),
        );
        // Optionally, clear the filter for product_type if needed.
        removeFilter('product_type');
      }

      setPlatforms(selectedPlatforms.join(','));
      setPlatformOptions(selectedPlatformOptions);
    },
    [
      platforms,
      platformOptions,
      setPlatforms,
      setPlatformOptions,
      removeFilter,
    ],
  );

  // Updated handleSelectPlatformOptions: When selecting an option,
  // ensure the parent platform is selected; if unselecting the last option for a platform,
  // remove the platform.
  const handleSelectPlatformOptions = useCallback(
    (checked: boolean, value: string, platform: SOCIAL_PLATFORMS) => {
      let selectedPlatformOptions = [...platformOptions];
      let selectedPlatforms = platforms ? platforms.split(',') : [];

      // Get all option objects for the given platform.
      const optionsForPlatform = PLATFORM_OPTIONS[platform] || [];
      const platformOptionValues = optionsForPlatform.map(
        (option) => option.value,
      );

      if (checked) {
        // Add option if not present.
        if (!selectedPlatformOptions.includes(value)) {
          selectedPlatformOptions.push(value);
        }
        // Ensure the parent platform is selected.
        if (!selectedPlatforms.includes(platform)) {
          selectedPlatforms.push(platform);
        }
        // If 1 or 2 options are selected, remove TikTok and YouTube
        if (
          selectedPlatformOptions.length > 0 &&
          selectedPlatformOptions.length < 3
        ) {
          selectedPlatforms = selectedPlatforms.filter(
            (p) =>
              p !== SOCIAL_PLATFORMS.tiktok && p !== SOCIAL_PLATFORMS.youtube,
          );
        }
        setPlatforms(selectedPlatforms.join(','));
      } else {
        // Remove the option.
        selectedPlatformOptions = selectedPlatformOptions.filter(
          (p) => p !== value,
        );

        // Check if any option for the platform is still selected.
        const hasAnyOption = selectedPlatformOptions.some(
          (opt: OptionsValueType) => platformOptionValues.includes(opt),
        );

        // If no options remain, remove the platform.
        if (!hasAnyOption) {
          selectedPlatforms = selectedPlatforms.filter((p) => p !== platform);
          setPlatforms(selectedPlatforms.join(','));
        }
      }

      // Update filters based on options selected.
      if (selectedPlatformOptions.length < 3) {
        const platformOptionsUpperCase = selectedPlatformOptions.map((po) =>
          po.toUpperCase(),
        );
        if (platformOptionsUpperCase.length === 0) {
          removeFilter('product_type');
        } else {
          const newFilter = {
            comparator: 'contains' as ComparatorType,
            name: 'product_type',
            value: platformOptionsUpperCase,
          };
          setFilters(newFilter);
        }
      } else {
        removeFilter('product_type');
      }

      setPlatformOptions(selectedPlatformOptions);
    },
    [
      platformOptions,
      platforms,
      setPlatformOptions,
      setFilters,
      removeFilter,
      setPlatforms,
    ],
  );

  return (
    <>
      <Button variant="tertiary" onClick={handlePlatformDrawerClick} size="lg">
        <Icon name="Filter" className="size-4" />
        Platform{' '}
        {platforms.length > 0 ? `(${platforms.split(',').length})` : null}
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handlePlatformDrawerClick}
      >
        <DrawerContent>
          <DrawerHeader>
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex items-center gap-2">
                <IconButton
                  aria-label="Close platform filter"
                  icon={<ArrowLeftIcon boxSize={4} />}
                  onClick={handlePlatformDrawerClick}
                />

                <h5 className="text-20">Filter by:</h5>
              </div>

              <Button
                variant="tertiary"
                onClick={clearPlatformsAndOptions}
                size="md"
              >
                Clear
              </Button>
            </div>
          </DrawerHeader>

          <DrawerBody>
            <ul className="flex flex-col gap-1 px-2 py-1">
              {Object.values(SOCIAL_PLATFORMS).map((platform) => {
                const Icon = getPlatformIcon(platform as SOCIAL_PLATFORMS);
                const options = PLATFORM_OPTIONS[platform];
                const isDisabled =
                  (platform === SOCIAL_PLATFORMS.tiktok ||
                    platform === SOCIAL_PLATFORMS.youtube) &&
                  platformOptions.length > 0 &&
                  platformOptions.length < 3;

                return (
                  <li key={platform}>
                    <div className="flex items-center justify-between">
                      <div
                        className={`flex items-center gap-2 ${
                          isDisabled ? 'opacity-40' : ''
                        }`}
                      >
                        <Icon boxSize={4} />
                        <p className="text-14 font-500 text-neutral-900">
                          {SOCIAL_PLATFORMS_NAMES[platform]}
                        </p>
                      </div>
                      <Checkbox
                        checked={platforms?.includes(platform)}
                        disabled={isDisabled}
                        onChange={(e) =>
                          handleSelectPlatform(e.target.checked, platform)
                        }
                        aria-label="Select platform"
                      />
                    </div>
                    {options ? (
                      <ul className="flex flex-col gap-1 pl-6 pt-2">
                        {options?.map((option) => (
                          <li key={option.value}>
                            <div className="flex items-center justify-between">
                              <p className="text-14 text-neutral-900">
                                {option.friendlyValue}
                              </p>
                              <Checkbox
                                checked={platformOptions.includes(option.value)}
                                disabled={isDisabled}
                                onChange={(e) =>
                                  handleSelectPlatformOptions(
                                    e.target.checked,
                                    option.value,
                                    platform,
                                  )
                                }
                                aria-label="Select platform options"
                              />
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
